// calculators/mortgage/src/calculator.ts
// UI logic for the public Mortgage Refinance Calculator.
// Standard fixed-rate amortization math (monthly compounding). Compares two loan
// scenarios, models extra monthly payments, renders an amortization table, and
// warns about the long-term cost of rolling credit card debt into a mortgage.
//
// Self-contained: no import from shared/payoffSimulation. The chart utility from
// _shared/multiSeriesChart IS allowed (esbuild inlines it per-bundle).

import {
  renderMultiSeriesChart,
  seriesFromMonths,
  clearChart,
  type ChartSeries,
} from "../../_shared/multiSeriesChart";

const CALC_ID = "mortgage";
const CHART_ID = "mc-chart";

// ── Math types ───────────────────────────────────────────────────────────────

interface MortgageMonth {
  month: number;
  openingBalance: number;
  interestAccrued: number;
  paymentApplied: number;
  closingBalance: number;
}

interface SimulateResult {
  months: MortgageMonth[];
  totalInterest: number;
  actualPayoffMonth: number;
  monthsSaved: number;
  interestSaved: number;
}

export interface CCImpactResult {
  deltaMonthlyCost: number;
  /** Extra lifetime interest caused by the rolled-in CC debt. Honors extraMonthly. */
  totalCCCostOverLoan: number;
  /** Absolute monthly payment on (loanAmount + ccDebt) — not a delta. */
  withCcMonthlyPayment: number;
  /**
   * Absolute total interest on (loanAmount + ccDebt) over the term — not a
   * delta. Honors extraMonthly.
   */
  withCcTotalInterest: number;
  /** Full month series for (loanAmount + ccDebt) — feeds the chart. */
  withCcMonths: MortgageMonth[];
}

// ── Exported math functions (exported for testing) ───────────────────────────

/**
 * Standard amortization monthly payment.
 * Edge case: rate === 0 → M = principal / termMonths.
 */
export function calcMonthlyPayment(
  principal: number,
  annualRate: number,
  termMonths: number
): number {
  if (termMonths <= 0) return 0;
  const r = annualRate / 12;
  if (r === 0) return principal / termMonths;
  const pow = Math.pow(1 + r, termMonths);
  return (principal * r * pow) / (pow - 1);
}

/**
 * Month-by-month amortization simulation. Returns MortgageMonth[] for every
 * month until payoff or termMonths. `extraMonthly` accelerates the payoff.
 */
export function simulateMortgage(
  principal: number,
  annualRate: number,
  termMonths: number,
  extraMonthly = 0
): SimulateResult {
  const r = annualRate / 12;
  const M = calcMonthlyPayment(principal, annualRate, termMonths);
  const months: MortgageMonth[] = [];
  let openingBalance = principal;
  let totalInterest = 0;
  let actualPayoffMonth = termMonths;

  for (let month = 1; month <= termMonths; month++) {
    const interestAccrued = openingBalance * r;
    const paymentApplied = Math.min(
      openingBalance + interestAccrued,
      M + extraMonthly
    );
    const closingBalance = openingBalance + interestAccrued - paymentApplied;
    totalInterest += interestAccrued;
    months.push({
      month,
      openingBalance,
      interestAccrued,
      paymentApplied,
      closingBalance,
    });
    openingBalance = closingBalance;
    if (closingBalance <= 0.01) {
      actualPayoffMonth = month;
      break;
    }
  }

  const monthsSaved = termMonths - actualPayoffMonth;

  // Guard against infinite recursion: only compute the no-extra baseline when
  // there IS extra. With extra === 0, this simulation IS the baseline.
  let interestSaved = 0;
  if (extraMonthly > 0) {
    const baseline = simulateMortgage(principal, annualRate, termMonths, 0);
    interestSaved = baseline.totalInterest - totalInterest;
  }

  return { months, totalInterest, actualPayoffMonth, monthsSaved, interestSaved };
}

/**
 * Impact of rolling credit-card debt into the mortgage.
 * In compare mode the caller passes Loan B's rate+term; in single mode, Loan A's.
 */
export function calcCCImpact(
  loanAmount: number,
  ccDebt: number,
  annualRate: number,
  termMonths: number,
  extraMonthly = 0
): CCImpactResult {
  const paymentAlone = calcMonthlyPayment(loanAmount, annualRate, termMonths);
  const paymentWithCC = calcMonthlyPayment(
    loanAmount + ccDebt,
    annualRate,
    termMonths
  );
  const deltaMonthlyCost = paymentWithCC - paymentAlone;

  const interestAlone = simulateMortgage(
    loanAmount,
    annualRate,
    termMonths,
    extraMonthly
  ).totalInterest;
  const simWithCC = simulateMortgage(
    loanAmount + ccDebt,
    annualRate,
    termMonths,
    extraMonthly
  );
  const interestWithCC = simWithCC.totalInterest;
  const totalCCCostOverLoan = interestWithCC - interestAlone;

  return {
    deltaMonthlyCost,
    totalCCCostOverLoan,
    withCcMonthlyPayment: paymentWithCC,
    withCcTotalInterest: interestWithCC,
    withCcMonths: simWithCC.months,
  };
}

/**
 * Per-loan CC roll-in impacts. `a` is ALWAYS modeled on Loan A's rate/term —
 * in both single and compare modes. `b` exists only in compare mode. Both are
 * null when there is no CC debt.
 */
export function computeCcImpacts(
  amount: number,
  ccDebt: number,
  rateA: number,
  termA: number,
  rateB: number,
  termB: number,
  mode: "single" | "compare",
  extra: number
): { a: CCImpactResult | null; b: CCImpactResult | null } {
  const a = ccDebt > 0 ? calcCCImpact(amount, ccDebt, rateA, termA, extra) : null;
  const b =
    mode === "compare" && ccDebt > 0
      ? calcCCImpact(amount, ccDebt, rateB, termB, extra)
      : null;
  return { a, b };
}

// ── PITI (escrow) breakdown — snapshot only, NEVER touches amortization ─────

export interface EscrowInputs {
  homeValue: number;
  taxAnnual: number;
  insAnnual: number;
  hoaMonthly: number;
  pmiMonthly: number;
}

export interface PitiBreakdown {
  pi: number;
  taxMonthly: number;
  insMonthly: number;
  hoaMonthly: number;
  pmiMonthly: number;
  totalMonthly: number;
  hasEscrow: boolean;
}

/**
 * Snapshot PITI breakdown. Pure display math: escrow items are added on top of
 * the P&I payment and never feed back into the amortization simulation.
 * Negative inputs clamp to 0.
 */
export function calcPitiBreakdown(
  piMonthly: number,
  e: EscrowInputs
): PitiBreakdown {
  const taxMonthly = Math.max(0, e.taxAnnual) / 12;
  const insMonthly = Math.max(0, e.insAnnual) / 12;
  const hoaMonthly = Math.max(0, e.hoaMonthly);
  const pmiMonthly = Math.max(0, e.pmiMonthly);
  const escrowTotal = taxMonthly + insMonthly + hoaMonthly + pmiMonthly;
  return {
    pi: piMonthly,
    taxMonthly,
    insMonthly,
    hoaMonthly,
    pmiMonthly,
    totalMonthly: piMonthly + escrowTotal,
    hasEscrow: escrowTotal > 0,
  };
}

/**
 * Suggested monthly PMI in whole dollars (0.5%/yr of the loan amount).
 * Returns 0 when home value is unknown (<= 0) or LTV <= 80%.
 */
export function suggestedPmiMonthly(
  loanAmount: number,
  homeValue: number
): number {
  if (homeValue <= 0 || loanAmount <= 0) return 0;
  if (loanAmount / homeValue <= PMI_LTV_THRESHOLD) return 0;
  return Math.round((loanAmount * SUGGESTED_PMI_ANNUAL_RATE) / 12);
}

// ── URL encode/decode helpers (internal only) ────────────────────────────────

const INPUT_KEYS = [
  "amount",
  "rateA",
  "termA",
  "extra",
  "ccDebt",
  "mode",
  "rateB",
  "termB",
  "homeValue",
  "taxAnnual",
  "insAnnual",
  "hoaMonthly",
  "pmiMonthly",
] as const;

type InputKey = (typeof INPUT_KEYS)[number];

function encodeInputsToUrl(inputs: Record<string, string>): void {
  const p = new URLSearchParams(window.location.search);
  for (const [k, v] of Object.entries(inputs)) p.set(k, v);
  history.replaceState(null, "", "?" + p.toString());
}

function decodeInputsFromUrl(): Partial<Record<InputKey, string>> {
  const p = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};
  for (const k of INPUT_KEYS) {
    const v = p.get(k);
    if (v !== null) result[k] = v;
  }
  return result;
}

// ── localStorage helpers (internal only) ─────────────────────────────────────

const LS_KEY = "mc-inputs";

function saveToStorage(inputs: Record<string, string>): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(inputs));
  } catch {
    /* quota / disabled */
  }
}

function loadFromStorage(): Partial<Record<string, string>> {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}") as Partial<
      Record<string, string>
    >;
  } catch {
    return {};
  }
}

// ── Small helpers ────────────────────────────────────────────────────────────

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function t(
  key: string,
  fallback: string,
  params?: Record<string, string | number>
): string {
  const w = window as unknown as {
    i18next?: { t: (k: string, p?: Record<string, string | number>) => string };
  };
  if (w.i18next && typeof w.i18next.t === "function") {
    const val = w.i18next.t(key, { defaultValue: fallback, ...(params || {}) });
    return typeof val === "string" ? val : fallback;
  }
  if (params) {
    return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      params[k] === undefined ? `{{${k}}}` : String(params[k])
    );
  }
  return fallback;
}

function gtag(...args: unknown[]): void {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag === "function") w.gtag(...args);
}

// Current site locale — same source/priority as js/i18n.js and store-tracking.js
// (i18next active language → <html lang> → ?lang= → 'en'). Sent on every event.
function getLang(): string {
  const SUPPORTED = ["en", "es", "fr", "en-GB", "en-AU", "en-CA", "fr-CA"];
  try {
    const w = window as unknown as {
      i18next?: { language?: string };
      getSiteLang?: () => string;
    };
    if (typeof w.getSiteLang === "function") return w.getSiteLang();
    if (w.i18next && typeof w.i18next.language === "string" && w.i18next.language) {
      return w.i18next.language;
    }
    const docLang = document.documentElement.getAttribute("lang");
    if (docLang && SUPPORTED.indexOf(docLang) !== -1) return docLang;
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param && SUPPORTED.indexOf(param) !== -1) return param;
  } catch {
    /* fall through */
  }
  return "en";
}

function formatCurrency(n: number): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `$${Math.round(n).toLocaleString()}`;
  }
}

function formatPayoffDate(actualPayoffMonth: number): string {
  const years = Math.floor(actualPayoffMonth / 12);
  const months = actualPayoffMonth % 12;
  return `${years} yr ${months > 0 ? months + " mo" : ""}`.trim();
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[\s,$]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function parsePctRaw(raw: string): number | null {
  const cleaned = raw.replace(/[\s,%]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function ccDebtBucket(ccDebt: number): string {
  if (ccDebt < 5000) return "<5k";
  if (ccDebt < 20000) return "5-20k";
  if (ccDebt < 50000) return "20-50k";
  return "50k+";
}

// ── Validation / parsing of the live form ────────────────────────────────────

const MAX_AMOUNT = 10000000;
const MAX_RATE_PCT = 25;
const MAX_EXTRA = 100000;
const MAX_CC_DEBT = 500000;
const MAX_HOME_VALUE = 50_000_000;
const MAX_TAX_ANNUAL = 200_000;
const MAX_INS_ANNUAL = 100_000;
const MAX_HOA_MONTHLY = 5_000;
const MAX_PMI_MONTHLY = 5_000;
const SUGGESTED_PMI_ANNUAL_RATE = 0.005;
const PMI_LTV_THRESHOLD = 0.80;

type Mode = "single" | "compare";

interface ParsedInputs {
  amount: number;
  rateA: number; // decimal
  termA: number; // months
  extra: number;
  ccDebt: number;
  mode: Mode;
  rateB: number; // decimal (only meaningful in compare mode)
  termB: number; // months
  homeValue: number;
  taxAnnual: number;
  insAnnual: number;
  hoaMonthly: number;
  pmiMonthly: number;
}

interface RawInputs {
  amount: string;
  rateA: string;
  termA: string;
  extra: string;
  ccDebt: string;
  mode: string;
  rateB: string;
  termB: string;
  homeValue: string;
  taxAnnual: string;
  insAnnual: string;
  hoaMonthly: string;
  pmiMonthly: string;
}

let currentMode: Mode = "single";

// True once the user has typed a PMI value themselves; cleared only when the
// PMI field is cleared. While false, the PMI field auto-fills from
// suggestedPmiMonthly() as home value / loan amount change.
let pmiUserEdited = false;

function selectedTerm(group: "a" | "b"): number {
  for (const yr of [15, 20, 30]) {
    const el = $(`mc-term-${group}-${yr}`) as HTMLButtonElement | null;
    if (el && el.getAttribute("aria-pressed") === "true") return yr * 12;
  }
  return 360; // default 30yr
}

function readRawInputs(): RawInputs {
  return {
    amount: ($("mc-input-amount") as HTMLInputElement | null)?.value || "",
    rateA: ($("mc-input-rate-a") as HTMLInputElement | null)?.value || "",
    termA: String(selectedTerm("a")),
    extra: ($("mc-input-extra") as HTMLInputElement | null)?.value || "",
    ccDebt: ($("mc-input-cc-debt") as HTMLInputElement | null)?.value || "",
    mode: currentMode,
    rateB: ($("mc-input-rate-b") as HTMLInputElement | null)?.value || "",
    termB: String(selectedTerm("b")),
    homeValue: ($("mc-input-home-value") as HTMLInputElement | null)?.value || "",
    taxAnnual: ($("mc-input-tax-annual") as HTMLInputElement | null)?.value || "",
    insAnnual: ($("mc-input-ins-annual") as HTMLInputElement | null)?.value || "",
    hoaMonthly: ($("mc-input-hoa-monthly") as HTMLInputElement | null)?.value || "",
    pmiMonthly: ($("mc-input-pmi") as HTMLInputElement | null)?.value || "",
  };
}

interface ValidationResult {
  valid: boolean;
  values?: ParsedInputs;
  errors: Record<string, string>;
}

function validate(raw: RawInputs): ValidationResult {
  const errors: Record<string, string> = {};

  const amount = parseAmount(raw.amount);
  if (amount === null || amount <= 0) {
    errors.amount = t(
      "calculators.mortgage.error_amount_required",
      "Enter a loan amount greater than 0."
    );
  } else if (amount > MAX_AMOUNT) {
    errors.amount = t(
      "calculators.mortgage.error_amount_too_large",
      "Loan amount must be $10,000,000 or less."
    );
  }

  const rateA = parsePctRaw(raw.rateA);
  if (rateA === null || rateA <= 0) {
    errors.rateA = t(
      "calculators.mortgage.error_rate_required",
      "Enter an interest rate."
    );
  } else if (rateA > MAX_RATE_PCT) {
    errors.rateA = t(
      "calculators.mortgage.error_rate_too_large",
      "Rate must be 25% or less."
    );
  }

  const extra = raw.extra.trim() === "" ? 0 : parseAmount(raw.extra);
  if (extra === null || extra < 0) {
    errors.extra = t(
      "calculators.mortgage.error_extra_invalid",
      "Extra payment must be 0 or more."
    );
  } else if (extra > MAX_EXTRA) {
    errors.extra = t(
      "calculators.mortgage.error_extra_too_large",
      "Extra payment must be $100,000 or less."
    );
  }

  const ccDebt = raw.ccDebt.trim() === "" ? 0 : parseAmount(raw.ccDebt);
  if (ccDebt === null || ccDebt < 0) {
    errors.ccDebt = t(
      "calculators.mortgage.error_cc_debt_invalid",
      "Credit card debt must be 0 or more."
    );
  } else if (ccDebt > MAX_CC_DEBT) {
    errors.ccDebt = t(
      "calculators.mortgage.error_cc_debt_too_large",
      "Credit card debt must be $500,000 or less."
    );
  }

  // Optional escrow fields: blank → 0, must be >= 0, per-field caps.
  const homeValue = raw.homeValue.trim() === "" ? 0 : parseAmount(raw.homeValue);
  if (homeValue === null || homeValue < 0) {
    errors.homeValue = t(
      "calculators.mortgage.error_home_value_invalid",
      "Home value must be 0 or more."
    );
  } else if (homeValue > MAX_HOME_VALUE) {
    errors.homeValue = t(
      "calculators.mortgage.error_home_value_too_large",
      "Home value must be $50,000,000 or less."
    );
  }

  const taxAnnual = raw.taxAnnual.trim() === "" ? 0 : parseAmount(raw.taxAnnual);
  if (taxAnnual === null || taxAnnual < 0) {
    errors.taxAnnual = t(
      "calculators.mortgage.error_tax_annual_invalid",
      "Property tax must be 0 or more."
    );
  } else if (taxAnnual > MAX_TAX_ANNUAL) {
    errors.taxAnnual = t(
      "calculators.mortgage.error_tax_annual_too_large",
      "Property tax must be $200,000/yr or less."
    );
  }

  const insAnnual = raw.insAnnual.trim() === "" ? 0 : parseAmount(raw.insAnnual);
  if (insAnnual === null || insAnnual < 0) {
    errors.insAnnual = t(
      "calculators.mortgage.error_ins_annual_invalid",
      "Insurance must be 0 or more."
    );
  } else if (insAnnual > MAX_INS_ANNUAL) {
    errors.insAnnual = t(
      "calculators.mortgage.error_ins_annual_too_large",
      "Insurance must be $100,000/yr or less."
    );
  }

  const hoaMonthly = raw.hoaMonthly.trim() === "" ? 0 : parseAmount(raw.hoaMonthly);
  if (hoaMonthly === null || hoaMonthly < 0) {
    errors.hoaMonthly = t(
      "calculators.mortgage.error_hoa_invalid",
      "HOA dues must be 0 or more."
    );
  } else if (hoaMonthly > MAX_HOA_MONTHLY) {
    errors.hoaMonthly = t(
      "calculators.mortgage.error_hoa_too_large",
      "HOA dues must be $5,000/mo or less."
    );
  }

  const pmiMonthly = raw.pmiMonthly.trim() === "" ? 0 : parseAmount(raw.pmiMonthly);
  if (pmiMonthly === null || pmiMonthly < 0) {
    errors.pmiMonthly = t(
      "calculators.mortgage.error_pmi_invalid",
      "PMI must be 0 or more."
    );
  } else if (pmiMonthly > MAX_PMI_MONTHLY) {
    errors.pmiMonthly = t(
      "calculators.mortgage.error_pmi_too_large",
      "PMI must be $5,000/mo or less."
    );
  }

  let rateB: number | null = 0;
  if (raw.mode === "compare") {
    rateB = parsePctRaw(raw.rateB);
    if (rateB === null || rateB <= 0) {
      errors.rateB = t(
        "calculators.mortgage.error_rate_required",
        "Enter an interest rate."
      );
    } else if (rateB > MAX_RATE_PCT) {
      errors.rateB = t(
        "calculators.mortgage.error_rate_too_large",
        "Rate must be 25% or less."
      );
    }
  }

  if (Object.keys(errors).length === 0 && amount !== null && rateA !== null) {
    return {
      valid: true,
      values: {
        amount,
        rateA: rateA / 100,
        termA: Number(raw.termA),
        extra: extra ?? 0,
        ccDebt: ccDebt ?? 0,
        mode: raw.mode === "compare" ? "compare" : "single",
        rateB: (rateB ?? 0) / 100,
        termB: Number(raw.termB),
        homeValue: homeValue ?? 0,
        taxAnnual: taxAnnual ?? 0,
        insAnnual: insAnnual ?? 0,
        hoaMonthly: hoaMonthly ?? 0,
        pmiMonthly: pmiMonthly ?? 0,
      },
      errors,
    };
  }
  return { valid: false, errors };
}

function renderFieldError(elSuffix: string, message: string | null): void {
  const errEl = $(`mc-error-${elSuffix}`);
  const inputEl = $(`mc-input-${elSuffix}`);
  if (errEl) {
    errEl.textContent = message || "";
    errEl.style.display = message ? "block" : "none";
  }
  if (inputEl) {
    if (message) inputEl.setAttribute("aria-invalid", "true");
    else inputEl.removeAttribute("aria-invalid");
  }
}

// Maps a validation error key to its DOM element suffix.
const ERROR_FIELD_SUFFIX: Record<string, string> = {
  amount: "amount",
  rateA: "rate-a",
  rateB: "rate-b",
  extra: "extra",
  ccDebt: "cc-debt",
  homeValue: "home-value",
  taxAnnual: "tax-annual",
  insAnnual: "ins-annual",
  hoaMonthly: "hoa-monthly",
  pmiMonthly: "pmi",
};

// ── Results rendering ────────────────────────────────────────────────────────

function showResults(): void {
  const r = $("mc-results");
  if (r) r.style.display = "block";
}

function hideResults(): void {
  const r = $("mc-results");
  if (r) r.style.display = "none";
  clearChart(CHART_ID);
}

function buildAmortTable(months: MortgageMonth[]): void {
  const body = $("mc-amort-body");
  if (!body) return;
  body.innerHTML = "";
  for (let start = 0; start < months.length; start += 12) {
    const chunk = months.slice(start, start + 12);
    if (chunk.length === 0) break;
    const first = chunk[0];
    const last = chunk[chunk.length - 1];
    const interestForYear = chunk.reduce((s, m) => s + m.interestAccrued, 0);
    const principalForYear = first.openingBalance - last.closingBalance;
    const paymentForYear = chunk.reduce((s, m) => s + m.paymentApplied, 0);
    const remainingBalance = last.closingBalance;
    const yearLabel = String(Math.floor(start / 12) + 1);

    const tr = document.createElement("tr");
    const cells = [
      yearLabel,
      formatCurrency(paymentForYear),
      formatCurrency(principalForYear),
      formatCurrency(interestForYear),
      formatCurrency(remainingBalance),
    ];
    for (const c of cells) {
      const td = document.createElement("td");
      td.textContent = c;
      tr.appendChild(td);
    }
    body.appendChild(tr);
  }
}

function renderCompareColumn(
  label: string,
  monthly: number,
  sim: SimulateResult,
  ccImpact: CCImpactResult | null = null
): string {
  const monthlyLbl = t("calculators.mortgage.compare_monthly", "Monthly");
  const interestLbl = t(
    "calculators.mortgage.compare_total_interest",
    "Total interest"
  );
  const payoffLbl = t("calculators.mortgage.compare_payoff", "Payoff");
  let html =
    `<h3 style="margin-bottom:12px;">${label}</h3>` +
    `<p style="margin:4px 0;">${monthlyLbl}: <strong>${formatCurrency(monthly)}</strong></p>` +
    `<p style="margin:4px 0;">${interestLbl}: <strong>${formatCurrency(sim.totalInterest)}</strong></p>` +
    `<p style="margin:4px 0;">${payoffLbl}: <strong>${formatPayoffDate(sim.actualPayoffMonth)}</strong></p>`;
  if (ccImpact) {
    const ccMonthlyLbl = t(
      "calculators.mortgage.compare_cc_row_monthly",
      "CC roll-in: payment increase"
    );
    const ccInterestLbl = t(
      "calculators.mortgage.compare_cc_row_interest",
      "CC roll-in: added interest"
    );
    html +=
      `<p style="margin:4px 0;">${ccMonthlyLbl}: <strong>+${formatCurrency(ccImpact.deltaMonthlyCost)}</strong></p>` +
      `<p style="margin:4px 0;">${ccInterestLbl}: <strong>+${formatCurrency(ccImpact.totalCCCostOverLoan)}</strong></p>`;
  }
  return html;
}

function renderResults(v: ParsedInputs): void {
  const simA = simulateMortgage(v.amount, v.rateA, v.termA, v.extra);
  const monthlyA = calcMonthlyPayment(v.amount, v.rateA, v.termA);

  // CC roll-in impacts — computed once. Tiles and the chart's with-CC series
  // are Loan-A-based in BOTH modes; the compare panel shows both loans'
  // roll-in figures side by side.
  const { a: ccImpactA, b: ccImpactB } = computeCcImpacts(
    v.amount,
    v.ccDebt,
    v.rateA,
    v.termA,
    v.rateB,
    v.termB,
    v.mode,
    v.extra
  );
  // Inline "with CC debt rolled in" alt line for a metric tile. The label text
  // carries the meaning; the warning color is reinforcement only.
  const ccAltInline = (amount: number): string =>
    `<span class="mc-cc-alt">${t(
      "calculators.mortgage.cc_alt_inline",
      "{{amount}} with CC debt rolled in",
      { amount: formatCurrency(amount) }
    )}</span>`;

  // Single-loan headline metrics.
  const monthlyEl = $("mc-result-monthly");
  if (monthlyEl) {
    monthlyEl.innerHTML =
      t("calculators.mortgage.metric_monthly", "Monthly payment (P&I)") +
      `: <strong>${formatCurrency(monthlyA + v.extra)}</strong>` +
      (ccImpactA ? ccAltInline(ccImpactA.withCcMonthlyPayment + v.extra) : "");
  }
  const totalIntEl = $("mc-result-total-interest");
  if (totalIntEl) {
    totalIntEl.innerHTML =
      t("calculators.mortgage.metric_total_interest", "Total interest") +
      `: <strong>${formatCurrency(simA.totalInterest)}</strong>` +
      (ccImpactA ? ccAltInline(ccImpactA.withCcTotalInterest) : "");
  }
  const payoffEl = $("mc-result-payoff");
  if (payoffEl) {
    payoffEl.innerHTML =
      t("calculators.mortgage.metric_payoff", "Payoff time") +
      `: <strong>${formatPayoffDate(simA.actualPayoffMonth)}</strong>`;
  }

  // Extra payment savings banner.
  const extraBanner = $("mc-extra-savings");
  if (extraBanner) {
    if (v.extra > 0 && simA.monthsSaved > 0) {
      extraBanner.style.display = "block";
      extraBanner.innerHTML = t(
        "calculators.mortgage.extra_savings",
        "Paying {{extra}}/mo extra cuts {{time}} off your mortgage and saves {{interest}} in interest.",
        {
          extra: formatCurrency(v.extra),
          time: formatPayoffDate(simA.monthsSaved),
          interest: `<strong>${formatCurrency(simA.interestSaved)}</strong>`,
        }
      );
    } else {
      extraBanner.style.display = "none";
    }
  }

  // PITI tile + payment-composition breakdown (display-only; the amortization
  // simulation above is pure P&I and never sees escrow). In compare mode the
  // PITI figure keys off Loan A.
  const breakdown = calcPitiBreakdown(monthlyA + v.extra, {
    homeValue: v.homeValue,
    taxAnnual: v.taxAnnual,
    insAnnual: v.insAnnual,
    hoaMonthly: v.hoaMonthly,
    pmiMonthly: v.pmiMonthly,
  });
  const pitiTile = $("mc-result-piti");
  const pitiBd = $("mc-piti-breakdown");
  const piCaption = $("mc-pi-only-caption");
  if (breakdown.hasEscrow) {
    if (pitiTile) {
      pitiTile.style.display = "";
      pitiTile.innerHTML =
        t("calculators.mortgage.metric_piti", "Full monthly payment (PITI)") +
        `: <strong>${formatCurrency(breakdown.totalMonthly)}</strong>`;
    }
    if (pitiBd) {
      const segments: Array<{ label: string; value: number; color: string }> = [
        {
          label: t("calculators.mortgage.piti_legend_pi", "Principal & interest"),
          value: breakdown.pi,
          color: "#00876B",
        },
        {
          label: t("calculators.mortgage.piti_legend_tax", "Property tax"),
          value: breakdown.taxMonthly,
          color: "#2563EB",
        },
        {
          label: t("calculators.mortgage.piti_legend_ins", "Insurance"),
          value: breakdown.insMonthly,
          color: "#7C3AED",
        },
        {
          label: t("calculators.mortgage.piti_legend_hoa", "HOA"),
          value: breakdown.hoaMonthly,
          color: "#D97706",
        },
        {
          label: t("calculators.mortgage.piti_legend_pmi", "PMI"),
          value: breakdown.pmiMonthly,
          color: "#DC2626",
        },
      ].filter((s) => s.value > 0);
      const barHtml = segments
        .map(
          (s) =>
            `<div class="mc-piti-seg" style="width:${((s.value / breakdown.totalMonthly) * 100).toFixed(2)}%;background:${s.color};"></div>`
        )
        .join("");
      const legendHtml = segments
        .map(
          (s) =>
            `<span class="mc-piti-legend-item"><span class="mc-piti-swatch" style="background:${s.color};"></span>${s.label}: <strong>${formatCurrency(s.value)}</strong></span>`
        )
        .join("");
      pitiBd.innerHTML =
        `<h3>${t("calculators.mortgage.piti_breakdown_heading", "Where your monthly payment goes")}</h3>` +
        `<div class="mc-piti-bar">${barHtml}</div>` +
        `<div class="mc-piti-legend">${legendHtml}</div>`;
      pitiBd.style.display = "block";
    }
    if (piCaption) piCaption.style.display = "block";
  } else {
    if (pitiTile) pitiTile.style.display = "none";
    if (pitiBd) pitiBd.style.display = "none";
    if (piCaption) piCaption.style.display = "none";
  }

  // Chart series (Loan A always; Loan B in compare mode).
  const series: ChartSeries[] = [
    {
      label: t("calculators.mortgage.series_loan_a", "Loan A"),
      color: "#00876B",
      points: seriesFromMonths(simA.months),
    },
  ];

  let simB: SimulateResult | null = null;
  if (v.mode === "compare") {
    simB = simulateMortgage(v.amount, v.rateB, v.termB, v.extra);
    series.push({
      label: t("calculators.mortgage.series_loan_b", "Loan B"),
      color: "#2563EB",
      points: seriesFromMonths(simB.months),
    });
  }

  // With-CC-debt series: shows the rolled-in balance curve alongside the
  // base loan(s). Always Loan-A-based (single source with the tiles). In
  // compare mode the chart caps at THREE series — B-with-CC is not drawn.
  if (ccImpactA) {
    series.push({
      label:
        v.mode === "compare"
          ? t("calculators.mortgage.cc_alt_label_a", "Loan A with CC debt rolled in")
          : t("calculators.mortgage.cc_alt_label", "With CC debt rolled in"),
      color: "#B45309",
      points: seriesFromMonths(ccImpactA.withCcMonths),
    });
  }

  const svg = $(CHART_ID) as unknown as SVGSVGElement | null;
  if (svg)
    renderMultiSeriesChart(
      svg,
      series,
      t("calculators.mortgage.still_owing", "still owing")
    );

  // Amortization table (Loan A).
  buildAmortTable(simA.months);

  // Compare panel.
  const comparePanel = $("mc-compare-panel");
  if (comparePanel) {
    if (v.mode === "compare" && simB) {
      comparePanel.style.display = "block";
      const monthlyB = calcMonthlyPayment(v.amount, v.rateB, v.termB);
      const colA = $("mc-compare-a");
      const colB = $("mc-compare-b");
      if (colA)
        colA.innerHTML = renderCompareColumn(
          t("calculators.mortgage.compare_loan_a", "Loan A"),
          monthlyA + v.extra,
          simA,
          ccImpactA
        );
      if (colB)
        colB.innerHTML = renderCompareColumn(
          t("calculators.mortgage.compare_loan_b", "Loan B"),
          monthlyB + v.extra,
          simB,
          ccImpactB
        );
      const summaryEl = $("mc-compare-summary");
      if (summaryEl) {
        const diff = Math.abs(simA.totalInterest - simB.totalInterest);
        const amt = `<strong>${formatCurrency(diff)}</strong>`;
        summaryEl.innerHTML =
          simA.totalInterest > simB.totalInterest
            ? t(
                "calculators.mortgage.compare_b_saves",
                "Loan B saves {{amount}} in total interest.",
                { amount: amt }
              )
            : t(
                "calculators.mortgage.compare_b_costs",
                "Loan B costs {{amount}} more in total interest.",
                { amount: amt }
              );
      }
    } else {
      comparePanel.style.display = "none";
    }
  }

  // CC debt warning panel.
  const ccWarn = $("mc-cc-warning");
  if (ccWarn) {
    if (v.ccDebt > 0 && ccImpactA) {
      const impact = ccImpactA;

      ccWarn.style.display = "block";
      const headingEl = $("mc-cc-warning-heading");
      if (headingEl)
        headingEl.textContent = t(
          "calculators.mortgage.cc_warning_heading",
          "Rolling in this credit card debt costs more than you think"
        );
      const deltaEl = $("mc-cc-warning-delta");
      if (deltaEl)
        deltaEl.innerHTML = t(
          "calculators.mortgage.cc_warning_delta",
          "Adding {{ccDebt}} raises your payment by {{delta}}/mo for the life of the loan.",
          {
            ccDebt: formatCurrency(v.ccDebt),
            delta: `<strong>${formatCurrency(impact.deltaMonthlyCost)}</strong>`,
          }
        );
      const totalEl = $("mc-cc-warning-total");
      if (totalEl)
        totalEl.innerHTML = t(
          "calculators.mortgage.cc_warning_total",
          "Over the full term, that {{ccDebt}} ends up costing {{total}} in extra mortgage interest.",
          {
            ccDebt: formatCurrency(v.ccDebt),
            total: `<strong>${formatCurrency(impact.totalCCCostOverLoan)}</strong>`,
          }
        );
      // Compare-only Loan B line (A-anchored panel above; B contrast here).
      const compareBEl = $("mc-cc-warning-compare");
      if (compareBEl) {
        if (ccImpactB) {
          compareBEl.style.display = "block";
          compareBEl.innerHTML = t(
            "calculators.mortgage.cc_warning_compare_b",
            "On Loan B ({{rate}}%, {{term}}-year), the same roll-in raises your payment by {{delta}}/mo and adds {{total}} in interest — see the loan comparison above.",
            {
              rate: (v.rateB * 100).toFixed(3).replace(/\.?0+$/, ""),
              term: String(v.termB / 12),
              delta: `<strong>${formatCurrency(ccImpactB.deltaMonthlyCost)}</strong>`,
              total: `<strong>${formatCurrency(ccImpactB.totalCCCostOverLoan)}</strong>`,
            }
          );
        } else {
          compareBEl.style.display = "none";
          compareBEl.innerHTML = "";
        }
      }
      const paycutEl = $("mc-cc-warning-paycut");
      if (paycutEl)
        paycutEl.textContent = t(
          "calculators.mortgage.cc_warning_paycut",
          "That is like giving yourself a permanent pay cut to keep paying off a debt you could have cleared in a few years."
        );
      const ctaEl = $("mc-cc-warning-cta");
      if (ctaEl)
        ctaEl.innerHTML = t(
          "calculators.mortgage.cc_warning_cta",
          "Before you roll it in, see what that card debt really costs in the Pay Down app."
        );

      gtag("event", "calc_cc_warning_shown", {
        calculator: CALC_ID,
        placement: "calculator",
        cc_debt_bucket: ccDebtBucket(v.ccDebt),
        lang: getLang(),
      });
    } else {
      ccWarn.style.display = "none";
    }
  }

  gtag("event", "calc_result_shown", {
    calculator: CALC_ID,
    placement: "calculator",
    mode: v.mode,
    term_months: v.termA,
    has_extra: v.extra > 0 ? "yes" : "no",
    has_cc_debt: v.ccDebt > 0 ? "yes" : "no",
    has_piti: breakdown.hasEscrow ? "yes" : "no",
    pmi_suggested: v.pmiMonthly > 0 && !pmiUserEdited ? "yes" : "no",
    lang: getLang(),
  });
}

// ── Run / debounce ───────────────────────────────────────────────────────────

const DEBOUNCE_MS = 300;
const GA_INPUT_DEBOUNCE_MS = 1000;
const ERROR_FIELDS = [
  "amount",
  "rateA",
  "rateB",
  "extra",
  "ccDebt",
  "homeValue",
  "taxAnnual",
  "insAnnual",
  "hoaMonthly",
  "pmiMonthly",
] as const;

let recalcTimer: number | null = null;
let gaInputTimer: number | null = null;
let lastChangedField: string | null = null;

function run(): void {
  const raw = readRawInputs();
  const v = validate(raw);

  for (const f of ERROR_FIELDS) {
    renderFieldError(ERROR_FIELD_SUFFIX[f], v.errors[f] || null);
  }

  if (!v.valid || !v.values) {
    hideResults();
    return;
  }

  try {
    showResults();
    renderResults(v.values);

    const rawForUrl: Record<string, string> = {
      amount: raw.amount,
      rateA: raw.rateA,
      termA: raw.termA,
      extra: raw.extra,
      ccDebt: raw.ccDebt,
      mode: raw.mode,
      rateB: raw.rateB,
      termB: raw.termB,
      homeValue: raw.homeValue,
      taxAnnual: raw.taxAnnual,
      insAnnual: raw.insAnnual,
      hoaMonthly: raw.hoaMonthly,
      pmiMonthly: raw.pmiMonthly,
    };
    encodeInputsToUrl(rawForUrl);
    saveToStorage(rawForUrl);
  } catch (err) {
    const msg =
      err instanceof Error ? err.message.slice(0, 80) : String(err).slice(0, 80);
    hideResults();
    const errBox = $("mc-runtime-error");
    if (errBox) {
      errBox.textContent = t(
        "calculators.mortgage.runtime_error",
        "Something went wrong. Try refreshing the page."
      );
      errBox.style.display = "block";
    }
    gtag("event", "calc_error", { calculator: CALC_ID, placement: "calculator", message: msg, lang: getLang() });
  }
}

function scheduleRecalc(): void {
  if (recalcTimer !== null) window.clearTimeout(recalcTimer);
  recalcTimer = window.setTimeout(() => {
    recalcTimer = null;
    run();
  }, DEBOUNCE_MS);
}

function scheduleGaInputEvent(): void {
  if (gaInputTimer !== null) window.clearTimeout(gaInputTimer);
  gaInputTimer = window.setTimeout(() => {
    gaInputTimer = null;
    if (lastChangedField) {
      gtag("event", "calc_input_change", {
        calculator: CALC_ID,
        placement: "calculator",
        field: lastChangedField,
        lang: getLang(),
      });
    }
  }, GA_INPUT_DEBOUNCE_MS);
}

// ── Mode toggle ──────────────────────────────────────────────────────────────

function setMode(mode: Mode): void {
  currentMode = mode;
  const single = $("mc-mode-single") as HTMLButtonElement | null;
  const compare = $("mc-mode-compare") as HTMLButtonElement | null;
  if (single)
    single.setAttribute("aria-pressed", mode === "single" ? "true" : "false");
  if (compare)
    compare.setAttribute("aria-pressed", mode === "compare" ? "true" : "false");

  const loanB = $("mc-loan-b-fields");
  if (loanB) loanB.style.display = mode === "compare" ? "" : "none";
}

// ── Term pills ───────────────────────────────────────────────────────────────

function setTerm(group: "a" | "b", years: number): void {
  for (const yr of [15, 20, 30]) {
    const el = $(`mc-term-${group}-${yr}`) as HTMLButtonElement | null;
    if (el) el.setAttribute("aria-pressed", yr === years ? "true" : "false");
  }
}

// ── Presets ──────────────────────────────────────────────────────────────────

interface Preset {
  amount: string;
  rateA: string;
  term: number; // years
  extra: string;
}

const PRESETS: Record<string, Preset> = {
  "mc-preset-30yr": { amount: "350000", rateA: "6.5", term: 30, extra: "0" },
  "mc-preset-15yr": { amount: "300000", rateA: "6.0", term: 15, extra: "0" },
  "mc-preset-extra": { amount: "400000", rateA: "7.0", term: 30, extra: "300" },
};

function applyPreset(id: string): void {
  const p = PRESETS[id];
  if (!p) return;
  const amountEl = $("mc-input-amount") as HTMLInputElement | null;
  const rateEl = $("mc-input-rate-a") as HTMLInputElement | null;
  const extraEl = $("mc-input-extra") as HTMLInputElement | null;
  if (amountEl) amountEl.value = p.amount;
  if (rateEl) rateEl.value = p.rateA;
  if (extraEl) extraEl.value = p.extra;
  setMode("single");
  setTerm("a", p.term);
  // Presets change the loan amount, which changes LTV — refresh the PMI
  // suggestion (no-op while the user owns the PMI field).
  updatePmiSuggestion();
  run();
}

// ── Input recovery on load ───────────────────────────────────────────────────

function recoverInputs(): boolean {
  const fromUrl = decodeInputsFromUrl();
  const fromStorage = loadFromStorage();
  const src: Partial<Record<string, string>> =
    Object.keys(fromUrl).length > 0 ? fromUrl : fromStorage;
  if (Object.keys(src).length === 0) return false;

  const amountEl = $("mc-input-amount") as HTMLInputElement | null;
  const rateAEl = $("mc-input-rate-a") as HTMLInputElement | null;
  const extraEl = $("mc-input-extra") as HTMLInputElement | null;
  const ccEl = $("mc-input-cc-debt") as HTMLInputElement | null;
  const rateBEl = $("mc-input-rate-b") as HTMLInputElement | null;
  const homeValueEl = $("mc-input-home-value") as HTMLInputElement | null;
  const taxEl = $("mc-input-tax-annual") as HTMLInputElement | null;
  const insEl = $("mc-input-ins-annual") as HTMLInputElement | null;
  const hoaEl = $("mc-input-hoa-monthly") as HTMLInputElement | null;
  const pmiEl = $("mc-input-pmi") as HTMLInputElement | null;

  if (src.amount && amountEl) amountEl.value = src.amount;
  if (src.rateA && rateAEl) rateAEl.value = src.rateA;
  if (src.extra && extraEl) extraEl.value = src.extra;
  if (src.ccDebt && ccEl) ccEl.value = src.ccDebt;
  if (src.rateB && rateBEl) rateBEl.value = src.rateB;
  if (src.homeValue && homeValueEl) homeValueEl.value = src.homeValue;
  if (src.taxAnnual && taxEl) taxEl.value = src.taxAnnual;
  if (src.insAnnual && insEl) insEl.value = src.insAnnual;
  if (src.hoaMonthly && hoaEl) hoaEl.value = src.hoaMonthly;
  if (src.pmiMonthly && pmiEl) {
    pmiEl.value = src.pmiMonthly;
    // Treat a recovered PMI value as user-owned so auto-suggest never
    // clobbers it (it may or may not have been a suggestion originally).
    pmiUserEdited = true;
  }

  if (src.termA) {
    const yrs = Math.round(Number(src.termA) / 12);
    if ([15, 20, 30].includes(yrs)) setTerm("a", yrs);
  }
  if (src.termB) {
    const yrs = Math.round(Number(src.termB) / 12);
    if ([15, 20, 30].includes(yrs)) setTerm("b", yrs);
  }

  if (src.mode === "compare") setMode("compare");
  else setMode("single");

  // Auto-run only if we have at least the core inputs.
  return Boolean((src.amount && amountEl) || (src.rateA && rateAEl));
}

// ── PMI auto-suggest ─────────────────────────────────────────────────────────

// While the user hasn't typed a PMI value, prefill the PMI field with the
// suggested estimate (0.5%/yr) whenever home value / loan amount imply
// LTV > 80%. Empty or missing home value → no suggestion (field cleared).
function updatePmiSuggestion(): void {
  if (pmiUserEdited) return;
  const pmiEl = $("mc-input-pmi") as HTMLInputElement | null;
  if (!pmiEl) return;
  const loanRaw = ($("mc-input-amount") as HTMLInputElement | null)?.value || "";
  const homeRaw = ($("mc-input-home-value") as HTMLInputElement | null)?.value || "";
  const loan = parseAmount(loanRaw) ?? 0;
  const home = parseAmount(homeRaw) ?? 0;
  const suggested = suggestedPmiMonthly(loan, home);
  pmiEl.value = suggested > 0 ? String(suggested) : "";
  const hint = $("mc-pmi-hint");
  if (hint) hint.style.display = suggested > 0 ? "block" : "none";
}

// ── Attach ───────────────────────────────────────────────────────────────────

export function attachCalculator(): void {
  // Default mode + term.
  setMode("single");
  setTerm("a", 30);
  setTerm("b", 30);

  // Text/number inputs.
  const textFields: Array<[string, string]> = [
    ["mc-input-amount", "amount"],
    ["mc-input-rate-a", "rateA"],
    ["mc-input-extra", "extra"],
    ["mc-input-cc-debt", "ccDebt"],
    ["mc-input-rate-b", "rateB"],
    ["mc-input-home-value", "homeValue"],
    ["mc-input-tax-annual", "taxAnnual"],
    ["mc-input-ins-annual", "insAnnual"],
    ["mc-input-hoa-monthly", "hoaMonthly"],
    ["mc-input-pmi", "pmiMonthly"],
  ];
  for (const [elId, fieldName] of textFields) {
    const el = $(elId) as HTMLInputElement | null;
    if (!el) continue;
    el.addEventListener("input", () => {
      lastChangedField = fieldName;
      const errBox = $("mc-runtime-error");
      if (errBox) errBox.style.display = "none";
      scheduleRecalc();
      scheduleGaInputEvent();
    });
  }

  // PMI auto-suggest: home value / loan amount changes refresh the suggestion
  // while the PMI field is untouched; typing in the PMI field takes ownership
  // (cleared only by clearing the field).
  const pmiInput = $("mc-input-pmi") as HTMLInputElement | null;
  if (pmiInput)
    pmiInput.addEventListener("input", () => {
      pmiUserEdited = pmiInput.value.trim() !== "";
      if (!pmiUserEdited) updatePmiSuggestion();
    });
  for (const id of ["mc-input-home-value", "mc-input-amount"]) {
    const el = $(id) as HTMLInputElement | null;
    if (el) el.addEventListener("input", updatePmiSuggestion);
  }

  // Mode pills.
  const single = $("mc-mode-single");
  if (single)
    single.addEventListener("click", () => {
      setMode("single");
      lastChangedField = "mode";
      run();
      scheduleGaInputEvent();
    });
  const compare = $("mc-mode-compare");
  if (compare)
    compare.addEventListener("click", () => {
      setMode("compare");
      lastChangedField = "mode";
      run();
      scheduleGaInputEvent();
    });

  // Term pills.
  for (const group of ["a", "b"] as const) {
    for (const yr of [15, 20, 30]) {
      const el = $(`mc-term-${group}-${yr}`);
      if (!el) continue;
      el.addEventListener("click", () => {
        setTerm(group, yr);
        lastChangedField = `term-${group}`;
        run();
        scheduleGaInputEvent();
      });
    }
  }

  // Preset buttons.
  for (const id of Object.keys(PRESETS)) {
    const el = $(id);
    if (el) el.addEventListener("click", () => applyPreset(id));
  }

  // Calculate button + form submit.
  const calcButton = $("mc-calculate") as HTMLButtonElement | null;
  if (calcButton)
    calcButton.addEventListener("click", (e) => {
      e.preventDefault();
      run();
    });
  const form = $("mc-form") as HTMLFormElement | null;
  if (form)
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      run();
    });

  // CTA tracking.
  document.querySelectorAll<HTMLAnchorElement>("[data-calc-cta]").forEach((a) => {
    a.addEventListener("click", () => {
      const store = a.getAttribute("data-calc-cta") || "unknown";
      const lang = getLang();
      // Keep calc_cta_click (it has GA history). The unified
      // `app_store_click` event is now owned solely by /js/store-tracking.js
      // (the global delegated listener) to avoid double-counting.
      gtag("event", "calc_cta_click", { calculator: CALC_ID, placement: "calculator", store, lang });
    });
  });

  gtag("event", "calc_view", { calculator: CALC_ID, placement: "calculator", lang: getLang() });

  // Recover inputs from URL / localStorage, then auto-run.
  if (recoverInputs()) run();
}
